/**
 * Selection Cost Utilities
 * 
 * Calculates estimated credit costs for selected nodes.
 */

import { Node, Edge } from '@xyflow/react';
import { calculateCreditCostWithResolution } from './creditCostUtils';
import { computeNodeBaseCredits } from './nodeCreditQuote';
import { isBillableNodeType } from './billableNodesUtils';

/**
 * Graph context for costing nodes whose price depends on what feeds them.
 *
 * Only ElevenLabs TTS needs it today: it is billed per character, and the
 * primary wiring is text-node → Voice, which leaves the node's own prompt
 * empty. Without the edges the estimate collapses to the 1-credit floor while
 * the server bills the full upstream paragraph. Optional so existing callers
 * that genuinely have no graph handy still compile — they just get the
 * prompt-only estimate.
 */
export interface CostGraphContext {
  edges: Edge[];
  nodes: Node[];
}

/** Text a node receives from its connected text input — mirrors readConnectedText in NodeAdapters.tsx. */
function connectedTextFor(nodeId: string, ctx?: CostGraphContext): string {
  if (!ctx) return '';
  for (const edge of ctx.edges) {
    if (edge.target !== nodeId) continue;
    const handle = edge.targetHandle;
    if (handle !== 'input-text' && handle !== 'input' && handle) continue;
    const source = ctx.nodes.find((n) => n.id === edge.source);
    const d = source?.data as Record<string, unknown> | undefined;
    if (!d) continue;
    for (const field of ['output', 'content', 'prompt'] as const) {
      const v = d[field];
      if (typeof v === 'string' && v.trim()) return v;
    }
  }
  return '';
}

/**
 * Default model IDs for each node type (used when selectedModel is not set)
 * These are synced with the actual defaults set in NodeAdapters.tsx and nodeDefinitions.ts
 * NOTE: Text models (agentPrompt, imagePrompt text generation) are not charged (cost = 0)
 */
export const NODE_TYPE_DEFAULT_MODELS: Record<string, string> = {
  'agentPrompt': 'gpt-5.4-nano',                        // Text model - FREE (0 credits)
  'geminiText': 'gemini-3.6-flash',                     // Google AI direct text node
  'grokText': 'grok-4.3',                               // xAI direct text node
  'imagePrompt': 'gemini-3.1-flash-image',              // Mirrors IMAGE_PROMPT_NODE_DEF selectedModel default (Banana 2; Lite still selectable)
  'fluxImage': 'flux-2-flash',                          // Matches nodeDefinitions FLUX dropdown default
  'zImageTurbo': 'z-image-turbo',                       // Hardcoded in NodeAdapters.tsx line 2028
  // Was 'qwen-image' (9 cr) while the node pins qwen-image-2 (16 cr) — a 44%
  // UNDER-quote, so the badge promised less than the bill charged.
  'qwenImage': 'qwen-image-2',                          // Mirrors nodeRegistry.QwenImage.initialData.selectedModel
  'qwenImageEdit': 'qwen-image-edit',                   // Hardcoded in NodeAdapters.tsx line 2030
  // Was 'seedream-4.5'. Same 18 cr as seedream-5-lite today, so nothing was
  // visibly wrong — and it would have diverged silently the first time either
  // id was repriced.
  'seedream': 'seedream-5-lite',                        // Mirrors nodeRegistry.Seedream.initialData.selectedModel
  'recraftV3': 'recraft-v4',                            // V4 is the new default (V3 still available via dropdown)
  'klingVideo': 'kling-3.0-standard',                   // Unified Kling node — default model (overridden by selectedModel)
  'kling26Video': 'kling-2.6',                          // matches actual selectedModel sent to server
  'kling25Video': 'kling-2.5-turbo',                    // From NodeAdapters.tsx line 3571
  'klingO3Video': 'kling-3.0-standard',                 // Kling 3.0 Standard (default quality)
  'wanVideo': 'wan-2.2',                                // From NodeAdapters.tsx line 3572
  'wan26Video': 'wan-2.7',                              // Wan 2.7 (default; 2.6 still selectable in node)
  'wanAnimateVideo': 'wan-animate-replace',             // Wan Animate V2V character animation
  'ltxVideo': 'ltx-2.3',                                // LTX node default: ltx-2.3 (Pro). T2V repriced server-side via ltx-2.3-t2v SKU.
  // LTX Fast hidden node default. Reaches its per-tier table (28/56/111,
  // migration 128) through calculateCreditCostWithResolution(), which now
  // routes ltx-2.3-fast into the shared calculateLtx23FastCreditCost() helper.
  // Before that it had no branch there at all and this path quoted
  // MODEL_BASE_COSTS x the resolution ladder — 27 at 1080p against a 19 charge.
  'ltxFastVideo': 'ltx-2.3-fast',
  'minimaxVideo': 'hailuo-2.3',                         // MiniMax node default: Hailuo 2.3 (Oct 2025)
  'pixVerseVideo': 'pixverse-6',                        // PixVerse node default: v6 (Nov 2025)
  'hunyuanVideo': 'hunyuan-1.5',                        // From NodeAdapters.tsx line 3576
  'klingAvatar': 'kling-avatar',                        // From NodeAdapters.tsx line 3578
  // Was 'veo-3-falai' (160 cr/s, legacy fal.ai VEO 3) while the Veo node has
  // only ever offered Google ids. The node pins veo-3.1-fast-generate-preview
  // in nodeRegistry.initialData, so this entry fires only for a node that
  // carries neither `model` nor `selectedModel` — and when it did, it quoted
  // 160 cr/s against a real 60, a 2.7x over-estimate.
  'veoVideo': 'veo-3.1-fast-generate-preview',          // Mirrors nodeRegistry.VeoVideo.initialData.selectedModel
  'geminiOmni': 'gemini-omni-1.1-flash',                 // Gemini Omni (Google direct) — 720p, priced like veo-3.1-fast. Must track the node default; the preview it replaced is shut down 2026-09-30.
  'seedance2Video': 'seedance-2-fast',                   // Seedance 2 Fast (default)
  'seedance25Video': 'seedance-2.5',                     // Seedance 2.5 (fal.ai) — single SKU
  // MiniMax H3 (fal.ai) — FOUR tiers at 24/28/60/74 cr/s (migration 127). This
  // path reaches them through calculateCreditCostWithResolution(), which routes
  // minimax-h3 into the SAME calculateMinimaxH3CreditCost() helper the node
  // badge and the Send button use, with the resolution read out of
  // parameters.resolution above. A node with no stored resolution falls back to
  // '720p' here, which the helper maps to 2K — the tier such a node actually
  // renders at — so the toolbar and the badge agree at every tier.
  'minimaxH3Video': 'minimax-h3',
  'inpaint': 'imagen-3-capability',                     // Fixed cost for inpainting
  'gptImage': 'gpt-image-2',                            // From nodeDefinitions.ts default (gpt-image-2)
  'grokImage': 'grok-image',                             // From nodeRegistry.ts initialData
  'grokVideo': 'grok-video',                             // Pricing key for standard Grok Video (1.5-preview uses its own ID)
  'glmImage': 'glm-image',                               // From nodeRegistry.ts initialData
  'klingImage': 'kling-image-o3',                         // From nodeRegistry.ts initialData
};

/**
 * Default resolutions per node type when not explicitly set by user.
 * Must match the fallback used in NodeAdapters.tsx creditCost useMemo getSetting() calls.
 */
const NODE_TYPE_DEFAULT_RESOLUTIONS: Record<string, string> = {
  'imagePrompt': '1024px',  // getSetting('resolution', '1024px') in NodeAdapters
  'gptImage': '1024px',     // getSetting('resolution', '1024px') in NodeAdapters
  // Kling Image is the ONE fal.ai image node that is NOT flat-priced: fal
  // charges the same for 1K and 2K but double for 4K, so it is deliberately
  // absent from FLAT_PRICING_IMAGE_NODE_TYPES and its real resolution setting
  // must reach calculateCreditCostWithResolution() below, which routes
  // kling-image-o3 to its own table before applying any multiplier.
  'klingImage': '1K',       // getSetting('resolution', '1K') in NodeAdapters
  // All other fal.ai image nodes and fluxImage use flat pricing ('720p' = base tier)
  // All video nodes default to '720p'
};

/**
 * Get the estimated credit cost for a single node (including batch count).
 *
 * Priced by computeNodeBaseCredits() — the SAME function the node's own credit
 * badge calls — so the toolbar total and the badge cannot disagree. It reads
 * settings where the handlers read them (`data.parameters`, falling back to the
 * definition default) and derives the fal.ai credit SKU the server reserves
 * under (`-audio`, `-t2v`). This function used to re-derive both on its own and
 * read `data.duration`, a field no node writes, so every video node was quoted
 * at 5 s whatever length it was set to.
 *
 * A node the shared quote prices as NaN (Kling Avatar with no measured audio, a
 * Veo resolution/length pair the server refuses) cannot run yet and adds 0.
 */
export function getNodeCreditCost(node: Node, ctx?: CostGraphContext): number {
  const nodeType = node.type;

  if (!isBillableNodeType(nodeType)) {
    return 0;
  }

  const data = (node.data || {}) as Record<string, any>;
  const parameters = data?.parameters as Record<string, string | number | undefined> || {};

  // Batch count — mirrors logic in NodeAdapters.tsx creditCost useMemo
  const rawBatch = parameters?.batchCount;
  const parsedBatch = typeof rawBatch === 'number' ? rawBatch : parseInt(String(rawBatch || '1'), 10);
  const batchCount = Math.min(10, Math.max(1, Number.isFinite(parsedBatch) ? parsedBatch : 1));

  const sources = ctx
    ? ctx.edges.filter((e) => e.target === node.id).map((e) => ctx.nodes.find((n) => n.id === e.source))
    : [];
  const quoted = computeNodeBaseCredits(nodeType!, data, {
    sources: sources as Array<{ type?: string; data?: Record<string, unknown> } | undefined>,
    connectedTextCharCount: connectedTextFor(node.id, ctx).length,
  });
  if (quoted !== undefined) {
    return Number.isFinite(quoted) ? quoted * batchCount : 0;
  }

  // Billable types the node badge has no quote for (agentPrompt, inpaint):
  // flat per generation off the model's registry price.
  const modelId = data?.model || data?.selectedModel || NODE_TYPE_DEFAULT_MODELS[nodeType!] || nodeType!;
  const defaultResolution = NODE_TYPE_DEFAULT_RESOLUTIONS[nodeType!] || '720p';
  const resolution = (typeof parameters?.resolution === 'string' && parameters.resolution)
    ? parameters.resolution
    : defaultResolution;
  const baseCost = calculateCreditCostWithResolution(String(modelId), 1, resolution);
  if (!Number.isFinite(baseCost)) return 0;
  return baseCost * batchCount;
}

/**
 * Get total credit cost for multiple nodes
 */
export function getTotalCreditCost(nodes: Node[], ctx?: CostGraphContext): number {
  return nodes.reduce((sum, node) => sum + getNodeCreditCost(node, ctx), 0);
}

/**
 * Get a breakdown of costs by node type
 */
export function getCostBreakdown(nodes: Node[], ctx?: CostGraphContext): Map<string, { count: number; cost: number }> {
  const breakdown = new Map<string, { count: number; cost: number }>();

  nodes.forEach(node => {
    const nodeType = node.type || 'unknown';
    const cost = getNodeCreditCost(node, ctx);
    
    if (cost > 0) {
      const existing = breakdown.get(nodeType) || { count: 0, cost: 0 };
      breakdown.set(nodeType, {
        count: existing.count + 1,
        cost: existing.cost + cost
      });
    }
  });
  
  return breakdown;
}
